// src/components/forms/SupplierForm.jsx
import React, { useState } from 'react';
import { Building2, ShieldCheck, AlertCircle, CheckCircle, Upload } from 'lucide-react';
import './SupplierForm.css';

export default function SupplierForm() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    password: '',
    gstNumber: '',
    aadharNumber: '',
    coreCategory: 'Manufacturing & Industrial Goods',
    catalogSummary: '',
    websiteUrl: ''
  });

  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // GST Verification States
  const [gstLoading, setGstLoading] = useState(false);
  const [isGstVerified, setIsGstVerified] = useState(false);

  // Aadhaar Verification States
  const [otpSent, setOtpSent] = useState(false);
  const [refId, setRefId] = useState('');
  const [otp, setOtp] = useState('');
  const [isAadhaarVerified, setIsAadhaarVerified] = useState(false);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);

  // Form State
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

 const coreCategories = [
  // Luxury & Lifestyle
  'Fine Jewelry, Gems & Precious Metals',
  'Watches & Luxury Timepieces',
  'Fashion Apparel, Textiles & Designer Wear',
  'Eyewear & Optical Boutiques',
  'Footwear & Leather Goods',

  // Electronics & Appliances
  'Consumer Electronics & Mobile Devices',
  'Home & Kitchen Appliances',
  'Air Conditioning, Cooling & Ventilation',

  // Home, Living & Construction
  'Interior Decor, Lighting & Furnishings',
  'Moulded Furniture & Modular Systems',
  'Hardware, Sanitaryware & Bath Fittings',
  'Building & Construction Materials',
  'Plastic Ware & Industrial Storage',

  // Health, Beauty & Wellness
  'Healthcare, Diagnostics & Pharmacy',
  'Fitness Centres, Spas & Salons',

  // Hospitality, Food & Entertainment
  'Fine Dining, Cafes & Restaurants',
  'Hotels, Resorts & Event Venues',
  'Gourmet Foods, Confectionery & Bakeries',

  // Automotive & Travel
  'Automobiles, Two-Wheelers & Dealerships',
  'Automotive Spare Parts, Tyres & Services',
  'Travel, Tours & Corporate Cab Services',

  // Professional & Business Services
  'Commercial Printing & Corporate Gifting',
  'Education, Coaching & Skill Institutes',
  'Other / Specialized Enterprise'
];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // 1. Verify GSTIN
  const handleVerifyGstin = async () => {
    if (!formData.gstNumber || formData.gstNumber.trim().length !== 15) {
      setErrorMessage('Please enter a valid 15-character GSTIN.');
      return;
    }

    setGstLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.lbnhyderabad.com/api/supplier/verify-gstin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ GSTIN: formData.gstNumber })
      });

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.message || 'GSTIN verification failed.');
      }

      setIsGstVerified(true);
      // Auto-fill company name if returned from GST records
      if (resData.data?.legalName && !formData.companyName) {
        setFormData((prev) => ({ ...prev, companyName: resData.data.legalName }));
      }
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setGstLoading(false);
    }
  };

  // 2. Send Aadhaar OTP
  const handleSendAadhaarOtp = async () => {
    if (!formData.aadharNumber || formData.aadharNumber.length < 12) {
      setErrorMessage('Please provide a valid 12-digit Aadhaar number.');
      return;
    }

    setAadhaarLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.lbnhyderabad.com/api/member/send-aadhar-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Aadhar_No: formData.aadharNumber })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to dispatch Aadhaar OTP');
      }

      setRefId(data.ref_id);
      setOtpSent(true);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setAadhaarLoading(false);
    }
  };

  // 3. Verify Aadhaar OTP
  const handleVerifyAadhaarOtp = async () => {
    if (!otp) {
      setErrorMessage('Please enter the OTP.');
      return;
    }

    setAadhaarLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.lbnhyderabad.com/api/member/verify-aadhar-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          otp,
          ref_id: refId,
          Aadhar_No: formData.aadharNumber
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Aadhaar verification unsuccessful');
      }

      setIsAadhaarVerified(true);
      if (data.verifiedName && !formData.contactPerson) {
        setFormData((prev) => ({ ...prev, contactPerson: data.verifiedName }));
      }
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setAadhaarLoading(false);
    }
  };

  // 4. Final Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!isGstVerified) {
      setErrorMessage('Please verify your enterprise GSTIN before submitting.');
      return;
    }

    if (!isAadhaarVerified) {
      setErrorMessage('Please verify the Authorized Signatory Aadhaar via OTP before submitting.');
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
        payload.append(key, formData[key]);
      });
      payload.append('gstVerified', isGstVerified);
      payload.append('aadhaarVerified', isAadhaarVerified);
      if (logo) {
        payload.append('logo', logo);
      }

      const response = await fetch('https://api.lbnhyderabad.com/api/supplier/register', {
        method: 'POST',
        body: payload
      });

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.message || 'Registration failed.');
      }

      setSuccessMessage(resData.message);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="supplier-onboard-viewport">
      <div className="supplier-form-card">
        <div className="supplier-header-banner">
          <h1>Supplier Enterprise Onboarding</h1>
          <p>Register your corporate entity, showcase commercial offerings, and provide exclusive privileges to accredited members.</p>
        </div>

        <div className="supplier-form-body">
          {errorMessage && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
              <AlertCircle size={18} />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage ? (
            <div style={{ textAlign: 'center', padding: '36px 16px' }}>
              <CheckCircle size={56} color="#166534" style={{ margin: '0 auto 14px' }} />
              <h2 style={{ color: '#00529b', fontFamily: 'Playfair Display, serif', fontSize: '1.6rem', marginBottom: '8px' }}>
                Corporate Registration Received
              </h2>
              <p style={{ color: '#475569', maxWidth: '480px', margin: '0 auto', fontSize: '0.85rem', lineHeight: '1.5' }}>
                {successMessage}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Section 1: Corporate & GST Verification */}
              <div className="section-divider-title">1. Corporate Identification & GSTIN</div>

              <div className="aadhaar-box" style={{ marginBottom: '16px' }}>
                <label className="field-label">GST Identification Number (GSTIN)</label>
                <div className="aadhaar-action-row">
                  <input
                    className="input-lux"
                    type="text"
                    name="gstNumber"
                    maxLength={15}
                    value={formData.gstNumber}
                    onChange={handleChange}
                    placeholder="Enter 15-digit GSTIN (e.g. 36AAAAA0000A1Z5)"
                    disabled={isGstVerified}
                    required
                  />
                  {!isGstVerified && (
                    <button
                      type="button"
                      className="btn-verify-action"
                      onClick={handleVerifyGstin}
                      disabled={gstLoading}
                    >
                      {gstLoading ? 'Validating...' : 'Verify GST'}
                    </button>
                  )}
                </div>
                {isGstVerified && (
                  <div className="verified-badge">
                    <ShieldCheck size={14} /> GST Identification Validated
                  </div>
                )}
              </div>

              <div className="form-group-full">
                <label className="field-label">Company / Legal Entity Name</label>
                <input
                  className="input-lux"
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Legal company title as per trade records"
                  required
                />
              </div>

              {/* Section 2: Contact Person, Aadhaar & Credentials */}
              <div className="section-divider-title">2. Authorized Signatory & Portal Access</div>

              <div className="form-grid-2">
                <div>
                  <label className="field-label">Authorized Contact Person</label>
                  <input
                    className="input-lux"
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="Full name of representative"
                    required
                  />
                </div>
                <div>
                  <label className="field-label">Corporate Email Address</label>
                  <input
                    className="input-lux"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="official@company.com"
                    required
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label className="field-label">Primary Mobile Number</label>
                  <input
                    className="input-lux"
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+91 98765 00000"
                    required
                  />
                </div>
                <div>
                  <label className="field-label">Portal Access Password</label>
                  <input
                    className="input-lux"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create login password"
                    required
                  />
                </div>
              </div>

              {/* Authorized Signatory Aadhaar Verification */}
              <div className="aadhaar-box">
                <label className="field-label">Signatory Aadhaar Validation</label>
                <div className="aadhaar-action-row">
                  <input
                    className="input-lux"
                    type="password"
                    name="aadharNumber"
                    maxLength={12}
                    value={formData.aadharNumber}
                    onChange={handleChange}
                    placeholder="Enter 12-digit Aadhaar of Representative"
                    disabled={isAadhaarVerified}
                    required
                  />
                  {!isAadhaarVerified && !otpSent && (
                    <button
                      type="button"
                      className="btn-verify-action"
                      onClick={handleSendAadhaarOtp}
                      disabled={aadhaarLoading}
                    >
                      {aadhaarLoading ? 'Sending...' : 'Send OTP'}
                    </button>
                  )}
                </div>

                {otpSent && !isAadhaarVerified && (
                  <div style={{ marginTop: '12px' }}>
                    <div className="aadhaar-action-row">
                      <input
                        className="input-lux"
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-verify-action"
                        onClick={handleVerifyAadhaarOtp}
                        disabled={aadhaarLoading}
                      >
                        {aadhaarLoading ? 'Verifying...' : 'Verify OTP'}
                      </button>
                    </div>
                  </div>
                )}

                {isAadhaarVerified && (
                  <div className="verified-badge">
                    <ShieldCheck size={14} /> Representative Identity Verified
                  </div>
                )}
              </div>

              {/* Section 3: Commercial Offerings & Catalog */}
              <div className="section-divider-title">3. Commercial Portfolio & Products</div>

              <div className="form-grid-2">
                <div>
                  <label className="field-label">Primary Sector / Category</label>
                  <select
                    className="input-lux"
                    name="coreCategory"
                    value={formData.coreCategory}
                    onChange={handleChange}
                  >
                    {coreCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Corporate Website / Storefront URL</label>
                  <input
                    className="input-lux"
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    placeholder="https://www.company.com"
                  />
                </div>
              </div>

              <div className="form-group-full">
                <label className="field-label">Product Portfolio & Offer Synopsis</label>
                <textarea
                  className="input-lux"
                  rows={2}
                  name="catalogSummary"
                  value={formData.catalogSummary}
                  onChange={handleChange}
                  placeholder="Detail primary product lines, models, or material solutions proposed for members (e.g., Heavy-duty exhaust fans, moulded plastic storage systems)."
                  required
                />
              </div>

              {/* Corporate Logo Uploader */}
              <div className="form-group-full">
                <label className="field-label">Corporate Brand Emblem / Logo</label>
                <div className="logo-upload-container">
                  <div className="logo-preview-box">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo Preview" className="logo-preview-img" />
                    ) : (
                      <Building2 size={26} color="#00529b" />
                    )}
                  </div>
                  <div>
                    <label htmlFor="supplier-logo-input" className="btn-file-select">
                      {logoPreview ? 'Change Emblem' : 'Attach Logo'}
                    </label>
                    {logoPreview && (
                      <button
                        type="button"
                        className="btn-file-remove"
                        onClick={() => {
                          setLogo(null);
                          setLogoPreview(null);
                        }}
                      >
                        Remove
                      </button>
                    )}
                    <input
                      id="supplier-logo-input"
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleLogoSelect}
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                      PNG or JPG format recommended for clear resolution on digital privilege cards.
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-submit-lux" disabled={loading}>
                {loading ? 'Submitting Registration...' : 'Submit Enterprise Onboarding'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}